import jsPDF from 'jspdf';

/**
 * @module GeradorPDF
 * @description Componente utilitário para geração de documentos PDF com suporte a cabeçalho
 * institucional com logotipo, tabelas, células, quebra automática de página e borda externa.
 * Baseado no padrão A4 portrait, utiliza jsPDF internamente.
 *
 * @example
 * // Uso básico
 * import GeradorPDF from '../components/GeradorPDF';
 *
 * const gerarRelatorio = async () => {
 *     const pdf = new GeradorPDF();
 *
 *     // Cabeçalho com logotipo
 *     await pdf.adicionarCabecalho({
 *         imagemSrc: '/brasao.jpg',
 *         linhas: [
 *             'MINISTÉRIO DA DEFESA',
 *             'EXÉRCITO BRASILEIRO',
 *             'COMANDO LOGÍSTICO',
 *             'CENTRO DE CONTROLE INTERNO'
 *         ]
 *     });
 *
 *     // Título
 *     pdf.adicionarTitulo('RELATÓRIO DE ANÁLISE 001/2026');
 *
 *     // Linha com células automáticas
 *     pdf.desenharLinha([
 *         { content: 'Empresa: ACME Ltda' },
 *         { content: 'CNPJ: 00.000.000/0001-00' }
 *     ]);
 *
 *     // Seção com header destacado
 *     pdf.desenharSecaoHeader('DADOS DO PRODUTO');
 *     pdf.desenharCelulaLarguraTotal('Produto: Arroz Tipo 1');
 *
 *     // Texto livre (sem borda)
 *     pdf.adicionarTexto('Observações: Nenhuma');
 *
 *     // Verificação de quebra de página
 *     pdf.verificarQuebraPagina();
 *
 *     // Assinatura
 *     pdf.adicionarAssinatura('Fulano de Tal', 'Inspetor Chefe');
 *
 *     // Finalizar e salvar
 *     pdf.salvar('relatorio_001.pdf');
 * };
 *
 * @example
 * // Configuração customizada
 * const pdf = new GeradorPDF({
 *     orientation: 'landscape',
 *     margin: 20,
 *     fontSize: { titulo: 14, subtitulo: 10, normal: 9, cabecalho: 11 },
 *     cores: { cinzaClaro: [200, 200, 200] },
 *     borderMargin: 12
 * });
 *
 * @example
 * // Tabela com colSpan
 * pdf.desenharSecaoHeader('CRITÉRIO MICROBIOLÓGICO');
 * pdf.desenharLinha([
 *     { content: 'ENSAIO', opcoes: { colSpan: 3, bold: true, align: 'center' } },
 *     { content: 'n', opcoes: { bold: true } },
 *     { content: 'c', opcoes: { bold: true } },
 *     { content: 'RESULTADO', opcoes: { colSpan: 3, bold: true, align: 'center' } }
 * ], 9);
 *
 * dados.forEach(item => {
 *     pdf.desenharLinha([
 *         { content: item.ensaio, opcoes: { colSpan: 3 } },
 *         { content: item.n },
 *         { content: item.c },
 *         { content: item.resultado, opcoes: { colSpan: 3 } }
 *     ], 9);
 * });
 */

/**
 * Configuração padrão do documento PDF
 * @typedef {Object} ConfiguracaoPDF
 * @property {'portrait'|'landscape'} [orientation='portrait'] - Orientação da página
 * @property {'mm'|'pt'|'cm'|'in'} [unit='mm'] - Unidade de medida
 * @property {'a4'|'letter'|'legal'} [format='a4'] - Formato da página
 * @property {number} [margin=15] - Margem interna do conteúdo (mm)
 * @property {number} [borderMargin=10] - Margem da borda externa (mm)
 * @property {ConfigFontSize} [fontSize] - Tamanhos de fonte
 * @property {ConfigCores} [cores] - Cores utilizadas
 */

/**
 * @typedef {Object} ConfigFontSize
 * @property {number} [titulo=11] - Tamanho da fonte do título
 * @property {number} [subtitulo=9] - Tamanho da fonte do subtítulo/header de seção
 * @property {number} [normal=8] - Tamanho da fonte normal
 * @property {number} [cabecalho=10] - Tamanho da fonte do cabeçalho institucional
 */

/**
 * @typedef {Object} ConfigCores
 * @property {number[]} [cinzaClaro=[220,220,220]] - Cor de fundo do header de seção [R, G, B]
 */

/**
 * Opções de formatação de célula
 * @typedef {Object} OpcoesCelula
 * @property {boolean} [bold=false] - Texto em negrito
 * @property {boolean} [italic=false] - Texto em itálico
 * @property {'left'|'center'|'right'} [align='left'] - Alinhamento horizontal
 * @property {'top'|'middle'|'bottom'} [verticalAlign='top'] - Alinhamento vertical
 * @property {number} [paddingX=2] - Espaçamento horizontal interno (mm)
 * @property {number} [paddingY=1] - Espaçamento vertical interno (mm)
 * @property {number} [lineHeight=3] - Altura de cada linha de texto (mm)
 * @property {number} [colSpan=1] - Número de colunas que a célula ocupa
 * @property {number} [rowSpan=1] - Número de linhas que a célula ocupa
 * @property {number} [rowHeight=5] - Altura padrão de uma linha para cálculo do rowSpan (mm)
 * @property {boolean} [border=true] - Se deve desenhar a borda da célula
 * @property {number} [fontSize=8] - Tamanho da fonte do texto (pt)
 * @property {number|null} [height=null] - Altura fixa da célula (mm). Se informado, ignora cálculo automático
 * @property {number[]|null} [bgColor=null] - Cor de fundo [R, G, B]
 */

/**
 * Item de célula para desenharLinha
 * @typedef {Object} ItemCelula
 * @property {string} content - Conteúdo de texto da célula
 * @property {OpcoesCelula} [opcoes] - Opções de formatação
 */

/**
 * Configuração do cabeçalho institucional
 * @typedef {Object} ConfigCabecalho
 * @property {string} imagemSrc - Caminho da imagem do logotipo/brasão
 * @property {string[]} linhas - Linhas de texto do cabeçalho (até 4 linhas)
 * @property {number} [larguraImagem=25] - Largura da imagem (mm)
 * @property {number} [alturaImagem=25] - Altura da imagem (mm)
 * @property {'JPEG'|'PNG'|'GIF'|'BMP'} [formatoImagem='JPEG'] - Formato da imagem
 */

class GeradorPDF {
    /**
     * Cria uma nova instância do GeradorPDF
     * @param {ConfiguracaoPDF} [config={}] - Configuração do documento
     */
    constructor(config = {}) {
        const {
            orientation = 'portrait',
            unit = 'mm',
            format = 'a4',
            margin = 15,
            borderMargin = 10,
            fontSize = {},
            cores = {},
            bordaExterna = true
        } = config;

        /** @type {jsPDF} Instância interna do jsPDF */
        this.doc = new jsPDF({ orientation, unit, format });
        this.doc.setLineWidth(0.2);

        /** @type {number} Largura da página (mm) */
        this.pageWidth = orientation === 'landscape' ? 297 : 210;
        /** @type {number} Altura da página (mm) */
        this.pageHeight = orientation === 'landscape' ? 210 : 297;
        /** @type {number} Margem do conteúdo (mm) */
        this.margin = margin;
        /** @type {number} Margem da borda externa (mm) */
        this.borderMargin = borderMargin;
        /** @type {number} Largura útil do conteúdo (mm) */
        this.contentWidth = this.pageWidth - (margin * 2);

        /** @type {ConfigFontSize} */
        this.fontSize = {
            titulo: 11,
            subtitulo: 9,
            normal: 8,
            cabecalho: 10,
            ...fontSize
        };

        /** @type {ConfigCores} */
        this.cores = {
            cinzaClaro: [220, 220, 220],
            ...cores
        };

        /** @type {number} Posição vertical atual do cursor (mm) */
        this.y = 15;

        /** @type {number} Contador de páginas */
        this._paginaAtual = 1;

        /** @type {boolean} Se deve desenhar borda externa nas páginas */
        this.bordaExterna = bordaExterna;
    }

    // ==================== UTILITÁRIOS ====================

    /**
     * Normaliza caracteres especiais não suportados pela fonte Helvetica do jsPDF
     * @param {string} text - Texto a ser normalizado
     * @returns {string} Texto com caracteres substituídos
     */
    normalizeText(text) {
        if (!text) return '';
        return String(text)
            .replace(/≤/g, '<=')
            .replace(/≥/g, '>=')
            .replace(/±/g, '+/-')
            .replace(/²/g, '2')
            .replace(/³/g, '3')
            .replace(/µ/g, 'u')
            .replace(/×/g, 'x')
            .replace(/÷/g, '/')
            .replace(/•/g, '-')
            .replace(/–/g, '-')
            .replace(/—/g, '-')
            .replace(/\u2018/g, "'")
            .replace(/\u2019/g, "'")
            .replace(/\u201C/g, '"')
            .replace(/\u201D/g, '"');
    }

    /**
     * Restaura a fonte para o estilo normal
     */
    fonteNormal() {
        this.doc.setFontSize(this.fontSize.normal);
        this.doc.setFont('helvetica', 'normal');
    }

    /**
     * Carrega uma imagem a partir de uma URL/src
     * @param {string} src - Caminho ou URL da imagem
     * @returns {Promise<HTMLImageElement>} Elemento de imagem carregado
     * @private
     */
    _loadImage(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    }

    /**
     * Retorna a instância interna do jsPDF para operações avançadas
     * @returns {jsPDF}
     */
    getDoc() {
        return this.doc;
    }

    /**
     * Retorna a posição Y atual do cursor
     * @returns {number} Posição Y em mm
     */
    getY() {
        return this.y;
    }

    /**
     * Define a posição Y do cursor manualmente
     * @param {number} novoY - Nova posição Y em mm
     */
    setY(novoY) {
        this.y = novoY;
    }

    /**
     * Avança o cursor Y por uma quantidade de mm
     * @param {number} espacamento - Quantidade em mm para avançar
     */
    avancarY(espacamento) {
        this.y += espacamento;
    }

    // ==================== CABEÇALHO ====================

    /**
     * Adiciona o cabeçalho institucional com logotipo e linhas de texto.
     * Deve ser chamado uma vez no início do documento.
     *
     * @param {ConfigCabecalho} config - Configuração do cabeçalho
     * @returns {Promise<void>}
     *
     * @example
     * await pdf.adicionarCabecalho({
     *     imagemSrc: '/brasao.jpg',
     *     linhas: [
     *         'MINISTÉRIO DA DEFESA',
     *         'EXÉRCITO BRASILEIRO',
     *         'COMANDO LOGÍSTICO',
     *         'CENTRO DE CONTROLE INTERNO'
     *     ]
     * });
     */
    async adicionarCabecalho(config) {
        const {
            imagemSrc,
            imagemData = null,
            linhas = [],
            larguraImagem = 25,
            alturaImagem = 25,
            formatoImagem = 'JPEG',
            alinhamento = 'direita',
            fontSize = this.fontSize.cabecalho,
            lineHeight = 5
        } = config;



        const imagemX = alinhamento === 'direita'
            ? this.pageWidth - this.borderMargin - larguraImagem
            : this.margin;

        // Adicionar imagem: usa imagemData (já carregada) ou carrega via imagemSrc
        if (imagemData) {
            try {
                this.doc.addImage(imagemData, formatoImagem, imagemX, this.y, larguraImagem, alturaImagem);
            } catch (e) {
                console.warn('GeradorPDF: Imagem do cabeçalho não inserida:', e);
            }
        } else if (imagemSrc) {
            try {
                const imagem = await this._loadImage(imagemSrc);
                this.doc.addImage(imagem, formatoImagem, imagemX, this.y, larguraImagem, alturaImagem);
            } catch (e) {
                console.warn('GeradorPDF: Imagem do cabeçalho não carregada:', e);
            }
        }

        // Texto do cabeçalho
        this.doc.setFontSize(fontSize);
        this.doc.setFont('helvetica', 'bold');
        const headerX = alinhamento === 'direita' ? this.margin : this.margin + larguraImagem + 5;
        linhas.forEach((linha, index) => {
            this.doc.text(linha, headerX, this.y + lineHeight + (index * lineHeight));
        });

        this.y = 47;
    }

    // ==================== CÉLULAS E LINHAS ====================

    /**
     * Calcula a altura necessária para uma célula sem desenhá-la.
     * Util para pré-calcular alturas antes de desenhar linhas.
     *
     * @param {number} [posicao=1] - Posição horizontal da célula (1-based)
     * @param {number} [divisoes=1] - Número total de colunas na linha
     * @param {string} [texto=' '] - Conteúdo de texto a calcular
     * @param {OpcoesCelula} [opcoes={}] - Opções de formatação
     * @returns {number} Altura calculada da célula (mm)
     */
    calcularAlturaCelula(posicao = 1, divisoes = 1, texto = ' ', opcoes = {}) {
        const {
            paddingX = 2,
            paddingY = 1,
            lineHeight = 3,
            colSpan = 1,
            fontSize: customFontSize = this.fontSize.normal
        } = opcoes;

        const singleCellWidth = this.contentWidth / divisoes;
        const cellWidth = singleCellWidth * colSpan;
        const textWidth = cellWidth - (paddingX * 2);

        this.doc.setFontSize(customFontSize);
        const textoNormalizado = this.normalizeText(texto ?? '');
        const linhas = this.doc.splitTextToSize(textoNormalizado, textWidth);

        const textTotalHeight = linhas.length * lineHeight;
        return Math.max(4, textTotalHeight + (paddingY * 2));
    }

    /**
     * Desenha uma célula posicional no PDF (baseada em grid de colunas).
     *
     * @param {number} [posicao=1] - Posição horizontal da célula (1-based)
     * @param {number} [divisoes=1] - Número total de colunas na linha
     * @param {string} [texto=' '] - Conteúdo de texto
     * @param {OpcoesCelula} [opcoes={}] - Opções de formatação
     * @returns {number} Altura da célula desenhada (mm)
     *
     * @example
     * // Célula na posição 1 de 3 colunas
     * pdf.desenharCelula(1, 3, 'Coluna 1');
     * pdf.desenharCelula(2, 3, 'Coluna 2', { bold: true });
     * pdf.avancarY(pdf.desenharCelula(3, 3, 'Coluna 3', { align: 'center' }));
     */
    desenharCelula(posicao = 1, divisoes = 1, texto = ' ', opcoes = {}) {
        const {
            bold = false,
            italic = false,
            align = 'left',
            verticalAlign = 'top',
            paddingX = 2,
            paddingY = 1,
            lineHeight = 3,
            colSpan = 1,
            rowSpan = 1,
            rowHeight = 5,
            border = true,
            fontSize: customFontSize = this.fontSize.normal,
            height = null,
            bgColor = null,
            color = null
        } = opcoes;

        const singleCellWidth = this.contentWidth / divisoes;
        const cellWidth = singleCellWidth * colSpan;
        const cellX = this.margin + (singleCellWidth * (posicao - 1));
        const textWidth = cellWidth - (paddingX * 2);

        this.doc.setFontSize(customFontSize);
        this.doc.setFont('helvetica', bold ? 'bold' : (italic ? 'italic' : 'normal'));

        const textoNormalizado = this.normalizeText(texto ?? '');
        const linhas = this.doc.splitTextToSize(textoNormalizado, textWidth);

        const textTotalHeight = linhas.length * lineHeight;
        const textHeight = textTotalHeight + (paddingY * 2);
        const spanHeight = rowSpan * rowHeight;
        const cellHeight = height !== null ? height : Math.max(4, textHeight, spanHeight);

        // Fundo
        if (bgColor) {
            this.doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
            this.doc.rect(cellX, this.y, cellWidth, cellHeight, 'F');
        }

        // Borda
        if (border) {
            this.doc.setLineWidth(0.2);
            this.doc.rect(cellX, this.y, cellWidth, cellHeight, 'S');
        }

        // Posição horizontal do texto
        let textX = cellX + paddingX;
        if (align === 'center') textX = cellX + (cellWidth / 2);
        if (align === 'right') textX = cellX + cellWidth - paddingX;

        // Posição vertical do texto
        let textY;
        if (verticalAlign === 'middle') {
            textY = this.y + (cellHeight - textTotalHeight) / 2 + 2.5;
        } else if (verticalAlign === 'bottom') {
            textY = this.y + cellHeight - textTotalHeight - paddingY + 2.5;
        } else {
            textY = this.y + paddingY + 2.5;
        }

        if (color) this.doc.setTextColor(color[0], color[1], color[2]);
        this.doc.text(linhas, textX, textY, { align });
        if (color) this.doc.setTextColor(0, 0, 0);
        return cellHeight;
    }

    /**
     * Desenha uma célula que ocupa toda a largura do conteúdo e avança o cursor Y automaticamente.
     *
     * @param {string} [texto=' '] - Conteúdo de texto
     * @param {OpcoesCelula} [opcoes={}] - Opções de formatação
     * @returns {number} Altura da célula desenhada (mm)
     *
     * @example
     * pdf.desenharCelulaLarguraTotal('Observações: Nenhuma observação registrada');
     * pdf.desenharCelulaLarguraTotal('Texto sem borda', { border: false });
     */
    desenharCelulaLarguraTotal(texto = ' ', opcoes = {}) {
        const altura = this.desenharCelula(1, 1, texto, opcoes);
        this.y += altura;
        return altura;
    }

    /**
     * Desenha uma linha de células com altura uniforme (a maior célula define a altura da linha).
     * Suporta colSpan para células que ocupam múltiplas colunas.
     *
     * @param {ItemCelula[]} celulas - Array de células a desenhar
     * @param {number} [totalColunas=0] - Total de colunas do grid (0 = inferir do array)
     * @returns {number} Altura da linha desenhada (mm)
     *
     * @example
     * // Linha simples com 2 colunas iguais
     * pdf.desenharLinha([
     *     { content: 'Empresa: ACME Ltda' },
     *     { content: 'CNPJ: 00.000.000/0001-00' }
     * ]);
     *
     * @example
     * // Linha com colSpan em grid de 12 colunas
     * pdf.desenharLinha([
     *     { content: 'ENSAIO', opcoes: { colSpan: 3 } },
     *     { content: 'n' },
     *     { content: 'c' },
     *     { content: 'RESULTADO', opcoes: { colSpan: 3 } }
     * ], 9);
     */
    desenharLinha(celulas, totalColunas = 0, options = {}) {
        const { border = true } = options;
        const divisoes = Math.max(totalColunas, celulas.length);

        // Calcular posições considerando colSpan
        let posicaoAtual = 1;
        const posicoesEColSpans = celulas.map((cel) => {
            const colSpan = cel.opcoes?.colSpan || 1;
            const posicao = posicaoAtual;
            posicaoAtual += colSpan;
            return { posicao, colSpan };
        });

        // Calcular altura máxima necessária
        const alturas = celulas.map((cel, index) =>
            this.calcularAlturaCelula(
                posicoesEColSpans[index].posicao,
                divisoes,
                cel.content,
                cel.opcoes || {}
            )
        );
        const maxHeight = Math.max(...alturas);

        // Desenhar todas as células com a mesma altura
        celulas.forEach((cel, index) => {
            this.desenharCelula(
                posicoesEColSpans[index].posicao,
                divisoes,
                cel.content,
                { border, ...cel.opcoes, height: maxHeight }
            );
        });

        this.y += maxHeight;
        return maxHeight;
    }

    // ==================== SEÇÕES E TÍTULOS ====================

    /**
     * Desenha um header de seção com fundo cinza e texto centralizado em negrito.
     *
     * @param {string} texto - Título da seção
     * @param {options} opcoes de impressao
     *
     * @example
     * pdf.desenharSecaoHeader('IDENTIFICAÇÃO DO ARTIGO');
     */
    desenharSecaoHeader(texto, options = {}) {
        const opts = {
            bold: true,
            align: 'center',
            verticalAlign: 'middle',
            fontSize: this.fontSize.subtitulo,
            bgColor: this.cores.cinzaClaro,
            height: 6,
            ...options
        };
        this.desenharCelula(1, 1, texto, opts);
        this.y += opts.height;
    }

    /**
     * Adiciona um título centralizado em negrito (sem borda, sem fundo).
     *
     * @param {string} texto - Texto do título
     * @param {number} [tamanhoFonte] - Tamanho da fonte (padrão: fontSize.titulo)
     *
     * @example
     * pdf.adicionarTitulo('RELATÓRIO DE ANÁLISE 001/2026');
     */
    adicionarTitulo(texto, tamanhoFonte) {
        this.doc.setFontSize(tamanhoFonte || this.fontSize.titulo);
        this.doc.setFont('helvetica', 'bold');
        this.doc.text(texto, this.pageWidth / 2, this.y, { align: 'center' });
        this.y += 8;
    }

    /**
     * Adiciona texto livre sem borda (atalho para desenharCelulaLarguraTotal com border: false).
     *
     * @param {string} texto - Texto a ser adicionado
     * @param {OpcoesCelula} [opcoes={}] - Opções adicionais de formatação
     * @returns {number} Altura ocupada (mm)
     *
     * @example
     * pdf.adicionarTexto('Resultado: Atende');
     * pdf.adicionarTexto('Texto centralizado', { align: 'center', bold: true });
     */
    adicionarTexto(texto, opcoes = {}) {
        return this.desenharCelulaLarguraTotal(texto, { border: false, ...opcoes });
    }

    // ==================== QUEBRA DE PÁGINA ====================

    /**
     * Verifica se a posição Y atual ultrapassa o limite e, se necessário,
     * desenha a borda externa da página atual e adiciona uma nova página.
     *
     * @param {number} [limite=260] - Posição Y limite para disparo da quebra (mm)
     * @returns {boolean} true se uma nova página foi adicionada, false caso contrário
     *
     * @example
     * // Verificação padrão (limite 260mm)
     * pdf.verificarQuebraPagina();
     *
     * // Limite customizado para conteúdo mais baixo
     * pdf.verificarQuebraPagina(230);
     */
    verificarQuebraPagina(limite = 260) {
        if (this.y > limite) {
            this.desenharBordaExterna();
            this.doc.addPage();
            this._paginaAtual++;
            this.y = this.margin;
            return true;
        }
        return false;
    }

    /**
     * Adiciona uma nova página manualmente e reposiciona o cursor.
     *
     * @param {number} [yInicial] - Posição Y inicial na nova página (padrão: margin)
     */
    novaPagina(yInicial) {
        this.desenharBordaExterna();
        this.doc.addPage();
        this._paginaAtual++;
        this.y = yInicial ?? this.margin;
    }

    // ==================== BORDA EXTERNA ====================

    /**
     * Desenha a borda externa da página atual.
     * Chamado automaticamente por verificarQuebraPagina() e novaPagina().
     * Pode ser chamado manualmente antes de salvar o documento.
     */
    desenharBordaExterna() {
        if (!this.bordaExterna) return;
        this.doc.setDrawColor(0);
        this.doc.rect(
            this.borderMargin,
            this.borderMargin,
            this.pageWidth - (this.borderMargin * 2),
            this.pageHeight - (this.borderMargin * 2)
        );
    }

    // ==================== ASSINATURA ====================

    /**
     * Adiciona bloco de assinatura centralizado com nome e função.
     * Posiciona automaticamente considerando o espaço disponível na página.
     *
     * @param {string} nome - Nome do responsável
     * @param {string} [funcao=''] - Função/cargo do responsável
     * @param {Object} [opcoes={}] - Opções adicionais
     * @param {boolean} [opcoes.comLinha=false] - Se deve desenhar linha de assinatura
     * @param {number} [opcoes.larguraLinha=80] - Largura da linha de assinatura (mm)
     *
     * @example
     * pdf.adicionarAssinatura('João da Silva', 'Inspetor Chefe');
     *
     * @example
     * pdf.adicionarAssinatura('Maria Santos', 'Coordenadora', { comLinha: true });
     */
    adicionarAssinatura(nome, funcao = '', opcoes = {}) {
        const { comLinha = false, larguraLinha = 80 } = opcoes;

        // Posicionar assinatura adequadamente
        if (this.y < 250) {
            this.y += 25;
        } else if (this.y > 275) {
            this.doc.addPage();
            this._paginaAtual++;
            this.y = 10;
        } else {
            this.y = 275;
        }

        // Linha de assinatura
        if (comLinha) {
            const linhaX = (this.pageWidth - larguraLinha) / 2;
            this.doc.line(linhaX, this.y, linhaX + larguraLinha, this.y);
            this.y += 5;
        }

        // Nome e função
        this.desenharCelulaLarguraTotal(nome, { align: 'center', border: false, bold: true });
        if (funcao) {
            this.desenharCelulaLarguraTotal(funcao, { align: 'center', border: false, bold: true });
        }
    }

    // ==================== OPÇÕES DE SELEÇÃO (CHECKBOX) ====================

    /**
     * Desenha uma linha com opções estilo checkbox (ex: "( X ) Sim" / "(   ) Não").
     * Útil para formulários com múltipla escolha.
     *
     * @param {string} label - Rótulo da pergunta
     * @param {Array<{texto: string, selecionado: boolean}>} opcoes - Opções disponíveis
     * @param {number} [colunasLabel=2] - Quantas colunas o label ocupa (de um grid de 6)
     *
     * @example
     * pdf.desenharOpcoes('Com o parecer do inspecionador:', [
     *     { texto: 'Concordo', selecionado: true },
     *     { texto: 'Não concordo', selecionado: false }
     * ]);
     */
    desenharOpcoes(label, opcoes, colunasLabel = 2) {
        const grid = 6;
        this.desenharCelula(1, grid, label, { colSpan: colunasLabel, align: 'right', border: false });

        opcoes.forEach((opcao, index) => {
            const marcador = opcao.selecionado ? '( X )' : '(   )';
            this.desenharCelula(colunasLabel + 1 + index, grid, `${marcador} ${opcao.texto}`, { border: false });
        });

        this.y += 6;
    }

    // ==================== LINHA HORIZONTAL ====================

    /**
     * Desenha uma linha horizontal simples na posição Y atual.
     *
     * @param {number} [espessura=0.2] - Espessura da linha (mm)
     */
    desenharLinhaHorizontal(espessura = 0.2) {
        this.doc.setLineWidth(espessura);
        this.doc.line(this.margin, this.y, this.margin + this.contentWidth, this.y);
    }

    // ==================== SALVAR / EXPORTAR ====================

    /**
     * Finaliza o documento desenhando a borda externa da última página e salva o PDF.
     *
     * @param {string} [nomeArquivo='documento.pdf'] - Nome do arquivo PDF para download
     *
     * @example
     * pdf.salvar('relatorio_001_2026.pdf');
     */
    salvar(nomeArquivo = 'documento.pdf') {
        this.desenharBordaExterna();
        this.doc.save(nomeArquivo);
    }

    /**
     * Retorna o PDF como Blob (útil para upload ou preview).
     *
     * @returns {Blob} Blob do PDF gerado
     *
     * @example
     * const blob = pdf.toBlob();
     * const url = URL.createObjectURL(blob);
     * window.open(url);
     */
    toBlob() {
        this.desenharBordaExterna();
        return this.doc.output('blob');
    }

    /**
     * Retorna o PDF como Data URI (útil para preview em iframe).
     *
     * @returns {string} Data URI do PDF
     *
     * @example
     * const dataUri = pdf.toDataUri();
     * document.getElementById('preview').src = dataUri;
     */
    toDataUri() {
        this.desenharBordaExterna();
        return this.doc.output('datauristring');
    }
}

export default GeradorPDF;